import { AxiosResponse } from "axios";
import { createServerApiClient } from "../server-base-client";

/**
 * The numbers a checkout summary needs to render. `PaymentIntentResponse`
 * satisfies it structurally; the $0 no-payment branch has no PaymentIntent
 * and supplies the same numbers from the promo-preflight quote.
 */
export type CheckoutTotals = {
  total: number;
  subTotal: number;
  product: {
    id: string;
    variant: string;
    price: number;
  };
  appliedAddons: Array<{
    name: string;
    price: number;
    id: string;
    key: string;
  }>;
  addons: Array<{
    name: string;
    price: number;
    id: string;
    key: string;
  }>;
  discount?: {
    code: string;
    type: "fixed" | "percentage";
    discount: number;
    freeAddon?: string;
  };
  taxAmount?: number;
};

export type PaymentIntentResponse = CheckoutTotals & {
  clientSecret: string;
  paymentIntentId: string;
  requiresAccount?: boolean;
};

export type PaymentStatusResponse = {
  paymentIntentId: string;
  total: number;
  subTotal: number;
  product: {
    id: string;
    variant: string;
    price: number;
  };
  appliedAddons?: Array<{
    id: string;
    name: string;
    price: number;
    key: string;
  }>;
  discount?: {
    code: string;
    type: "fixed" | "percentage";
    discount: number;
    freeAddon?: string;
  };
  status?: string;
  email?: string;
  taxAmount?: number;
  userId?: string;
  requiresAccount?: boolean;
};

export type PaymentChargeDto = {
  id: string;
  total: number;
  subTotal: number;
  billingDetails: {
    email: string;
    name: string;
    phone: string;
    address: {
      line1: string;
      line2: string;
    };
  };
};

type CreatePaymentIntentParams = {
  yzAnonymousId: string;
  product: string;
  email?: string;
  phone?: string;
  address?: string;
  addons?: string[];
  promotionalCode?: string;
  zipCode?: string;
  shouldSetupDesignProject?: boolean;
  userId?: string;
};

type UpdatePaymentIntentParams = {
  product: string;
  addons?: string[];
  promotionalCode?: string;
  email?: string;
  phone?: string;
  userId?: string;
  existingAccountEmail?: string;
  zipCode?: string;
  fullName?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
};

export type PromoPreflightParams = {
  product: string;
  promotionalCode: string;
  email?: string;
  addons?: string[];
};

export type PromoPreflightResponse = {
  outcome: "paid_checkout" | "no_payment_due" | "invalid_partner_promo";
  reason?: string;
  message?: string;
  /** Set on no_payment_due: the base-package totals the $0 UI renders. */
  quote?: CheckoutTotals;
  /** Set on no_payment_due when the request carried add-ons: the dropped keys. */
  unsupportedAddons?: string[];
  /**
   * True when the code carries builder-partner metadata (regardless of
   * outcome) — the post-payment redirect keys partner routing off this.
   */
  builderPartnerCode?: boolean;
};

export type RedeemNoPaymentParams = {
  product: string;
  promotionalCode: string;
  email: string;
  userId: string;
  yzAnonymousId?: string;
  /** Must be [] — sent so the server can reject a non-empty cart. */
  addons?: string[];
  fullName?: string;
};

export type RedeemNoPaymentResponse = {
  outcome: "redeemed" | "already_redeemed";
  designProjectId: string;
  /**
   * Buyer-facing order reference for the success dialog — there is no
   * paymentIntentId. Server-generated; the client must never compose one.
   */
  orderId: string;
  /** The totals actually redeemed (total 0). */
  quote: CheckoutTotals;
};

export type FindUserProjectByEmailResponse = {
  userId: string;
  projects: string[];
};

const getApiClient = async (authToken?: string) => {
  const client = await createServerApiClient(authToken);

  // Add API key header to the client's default headers
  client.defaults.headers.common["x-api-key"] = process.env.PAYMENTS_V2_API_KEY;

  return client;
};

export async function createPaymentIntent(
  params: CreatePaymentIntentParams,
  idempotencyKey?: string,
  authToken?: string,
): Promise<AxiosResponse<PaymentIntentResponse>> {
  const client = await getApiClient(authToken);

  const headers = idempotencyKey
    ? {
        "Idempotency-Key": idempotencyKey,
      }
    : undefined;

  return await client.post(
    "payments-v2",
    {
      ...params,
    },
    { headers },
  );
}

export async function updatePaymentIntent(
  paymentIntentId: string,
  params: UpdatePaymentIntentParams,
  authToken?: string,
): Promise<AxiosResponse<PaymentIntentResponse>> {
  const client = await getApiClient(authToken);

  return await client.patch(`payments-v2/${paymentIntentId}`, {
    ...params,
  });
}

/**
 * Asks whether a promo code applies to a purchase before any PaymentIntent
 * exists — used by partner checkout links, which carry a code + email.
 */
export async function promoPreflight(
  params: PromoPreflightParams,
  authToken?: string,
): Promise<AxiosResponse<PromoPreflightResponse>> {
  const client = await getApiClient(authToken);

  return await client.post("payments-v2/promo-preflight", {
    ...params,
  });
}

/**
 * Redeems a builder-funded 100% code with no payment (EN-5794). Eligibility
 * is re-derived server-side; a 400 means the binding broke, a 409 means the
 * code was already redeemed by a different account.
 */
export async function redeemNoPayment(
  params: RedeemNoPaymentParams,
  authToken?: string,
): Promise<AxiosResponse<RedeemNoPaymentResponse>> {
  const client = await getApiClient(authToken);

  return await client.post("payments-v2/redeem-no-payment", {
    ...params,
  });
}

export async function getPaymentIntentStatus(
  paymentIntentId: string,
  authToken?: string,
): Promise<AxiosResponse<PaymentStatusResponse>> {
  const client = await getApiClient(authToken);

  try {
    return await client.get(`payments-v2/${paymentIntentId}/status`);
  } catch (error) {
    console.error("Error getting payment intent status:", error);
    throw error;
  }
}

export async function getLatestCharge(
  paymentIntentId: string,
  authToken?: string,
): Promise<AxiosResponse<PaymentChargeDto>> {
  const client = await getApiClient(authToken);
  return await client.get(`payments-v2/${paymentIntentId}/latest-charge`);
}

export async function findUserByEmail(
  email: string,
  authToken?: string,
): Promise<AxiosResponse<FindUserProjectByEmailResponse>> {
  const client = await getApiClient(authToken);

  // First, try to find user in Firebase Auth
  const firebaseResult = await client.get(`auth-util/find-by-email`, {
    params: { email },
  });

  // If user found in Firebase, return immediately
  if (firebaseResult.data.userId) {
    return firebaseResult;
  }

  // If not found in Firebase, check Prisma user_profile table as fallback
  console.log(
    `User not found in Firebase for email ${email}, checking banyan DB...`,
  );

  const prismaResult = await client.get(`auth-util/find-profile-by-email`, {
    params: { email },
  });

  return prismaResult;
}
